import * as stylex from '@stylexjs/stylex'
import { type ForwardedRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { TextareaAutosize } from '../TextareaAutosize/TextareaAutosize'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _baseStyles = stylex.create({
  root: {
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    display: 'inline-flex',
  },
  rootFullWidth: {
    width: '100%',
  },
  wrapper: {
    flex: '1',
    overflow: 'hidden',
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  adornment: {
    font: 'inherit',
    overflow: 'hidden',
    alignItems: 'center',
    display: 'inline-flex',
    // Use available space, shrinking only when content competes with the
    // input minimum. Clip from the start instead of centering overflow.
    flexShrink: 1,
    fontSize: 20,
    justifyContent: 'flex-start',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  startAdornment: {
    paddingRight: 4,
  },
  endAdornment: {
    paddingLeft: 4,
  },
  input: {
    'WebkitTapHighlightColor': 'transparent',
    'font': 'inherit',
    'margin': 0, // Reset for Safari
    'padding': 0,
    'borderWidth': 0,
    'outline': 'none',
    'backgroundColor': 'transparent',
    'backgroundImage': 'none',
    'boxShadow': 'none', // Reset for WP Flatsome
    'boxSizing': 'border-box',
    'color': colors.grey800,
    'display': 'block',
    /*
     * 在 wrapper flex 內撐滿剩餘空間。沒這個，input 元素只佔
     * intrinsic width — 視覺上看起來有 border 撐滿框，但實際
     * hit-test 點不到右半邊（adornment 也會擠到 input 旁而不是
     * 右端）。用 flexGrow longhand（StyleX 不接受 flex shorthand 數字）。
     */
    'flexBasis': 0,
    'flexGrow': 1,
    'fontFamily': typography.font,
    'fontSize': 'inherit',
    'fontWeight': typography.weightRegular,
    'letterSpacing': 'inherit',
    'lineHeight': 1.4287,
    // 值比欄位長時收成省略號，而不是硬切在半個字上（單行 input 才有效，聚焦時瀏覽器仍會捲動顯示）
    'textOverflow': 'ellipsis',
    'height': '1.4287em', // Reset to 20px
    'minHeight': 'initial', // Reset for WP Flatsome
    // Reserve an editable area even when both adornments exhaust the row.
    // The adornments shrink to share the remaining space.
    'minWidth': '4ch',
    '::placeholder': {
      color: colors.grey500,
      opacity: 1,
    },
  },
  textarea: {
    flexBasis: 'auto',
    resize: 'none',
    minWidth: 175,
  },
  disabled: {

    // fix for safari opacity bug
    WebkitTextFillColor: colors.grey600,
    color: colors.grey600,
    cursor: 'not-allowed',
    opacity: 1,
  },
  fullWidth: {
    width: '100%',
  },
})

export type InputBaseHTMLAttributes<Multiline extends boolean = false> = Multiline extends true
  ? TextareaHTMLAttributes<HTMLTextAreaElement>
  : InputHTMLAttributes<HTMLInputElement>

export type InputBaseProps<Multiline extends boolean = false> = {
  /**
   * 根元素自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 覆寫內部樣式
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 是否撐滿父容器寬度
   * @default false
   */
  fullWidth?: boolean
  /**
   * 輸入框前方裝飾元素
   */
  startAdornment?: ReactNode
  /**
   * 輸入框後方裝飾元素
   */
  endAdornment?: ReactNode
  /**
   * 是否為多行模式（textarea）
   * @default false
   */
  multiline?: Multiline
  /**
   * 多行模式下最少顯示列數
   */
  minRows?: number
  /**
   * 多行模式下最多顯示列數
   */
  maxRows?: number
  /**
   * 多行模式下固定顯示列數（同時設定 minRows 與 maxRows）
   */
  rows?: number
  /**
   * 多行模式下可見文字欄寬（cols 屬性）
   */
  cols?: number
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
  /**
   * 輸入元素的 ref
   */
  inputRef?: ForwardedRef<Multiline extends true ? HTMLTextAreaElement : HTMLInputElement>
  /**
   * 包住 adornment 與輸入元素那層的 ref。
   * 供外層量測捲動狀態（consumer 把 wrapper 設成捲動容器時）。
   */
  wrapperRef?: ForwardedRef<HTMLDivElement>
} & InputBaseHTMLAttributes<Multiline>

/**
 * InputBase 是所有輸入元件的底層基礎元件。
 * 提供 adornment、multiline 及樣式覆寫等功能，通常不直接使用，而是由 Input、Select 等元件內部引用。
 *
 * @example
 * ```tsx
 * <InputBase placeholder="請輸入" startAdornment={<SearchIcon />} fullWidth />
 * ```
 */
export const InputBase = <Multiline extends boolean = false>(props: InputBaseProps<Multiline>) => {
  const {
    rootStyle,
    xStyles,
    startAdornment,
    endAdornment,
    fullWidth,
    multiline,
    minRows: minRowsProp,
    maxRows: maxRowsProp,
    rows,
    cols,
    rootRef,
    inputRef,
    wrapperRef,
    disabled,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)

  const renderInput = () => {
    const inputStyleProps = [baseStyles.input, fullWidth && baseStyles.fullWidth, disabled && baseStyles.disabled]
    if (multiline) {
      let minRows = minRowsProp
      let maxRows = maxRowsProp
      if (rows) {
        minRows = rows
        maxRows = rows
      }
      return (
        <TextareaAutosize
          rootRef={inputRef as ForwardedRef<HTMLTextAreaElement>}
          {...stylex.props(inputStyleProps, baseStyles.textarea)}
          minRows={minRows}
          maxRows={maxRows}
          cols={cols}
          disabled={disabled}
          {...(rest as InputBaseHTMLAttributes<true>)}
        />
      )
    }
    return (
      <input
        ref={inputRef as ForwardedRef<HTMLInputElement>}
        disabled={disabled}
        {...stylex.props(inputStyleProps)}
        {...(rest as InputBaseHTMLAttributes<false>)}
      />
    )
  }

  const renderStartAdornment = () => {
    if (!startAdornment) return null
    return <div {...stylex.props(baseStyles.adornment, baseStyles.startAdornment)}>{startAdornment}</div>
  }

  const renderEndAdornment = () => {
    if (!endAdornment) return null
    return <div {...stylex.props(baseStyles.adornment, baseStyles.endAdornment)}>{endAdornment}</div>
  }

  return (
    <div ref={rootRef} {...stylex.props(baseStyles.root, fullWidth && baseStyles.rootFullWidth, rootStyle)}>
      <div ref={wrapperRef} {...stylex.props(baseStyles.wrapper)}>
        {renderStartAdornment()}
        {renderInput()}
        {renderEndAdornment()}
      </div>
    </div>
  )
}
